/**
 * Currencies the счёт can be converted into.
 *
 * `after: true` puts the symbol behind the amount, which is how ₽, ₸ and BYN
 * are written — the условия document already shows them that way.
 */
export const CURRENCIES = [
  { code: 'USD', symbol: '$',   after: false, locale: 'en-US', label: 'Доллар США', genitive: 'доллара' },
  { code: 'RUB', symbol: '₽',   after: true,  locale: 'ru-RU', label: 'Российский рубль', genitive: 'рубля' },
  { code: 'KZT', symbol: '₸',   after: true,  locale: 'ru-KZ', label: 'Казахстанский тенге', genitive: 'тенге' },
  { code: 'BYN', symbol: 'BYN', after: true,  locale: 'ru-BY', label: 'Белорусский рубль', genitive: 'белорусского рубля' },
  { code: 'EUR', symbol: '€',   after: false, locale: 'de-DE', label: 'Евро', genitive: 'евро' },
  { code: 'GEL', symbol: '₾',   after: true,  locale: 'ka-GE', label: 'Грузинский лари', genitive: 'лари' },
  { code: 'UAH', symbol: '₴',   after: true,  locale: 'uk-UA', label: 'Украинская гривна', genitive: 'гривны' },
  { code: 'KES', symbol: 'KSh', after: false, locale: 'en-KE', label: 'Кенийский шиллинг', genitive: 'шиллинга' },
  { code: 'GBP', symbol: '£',   after: false, locale: 'en-GB', label: 'Фунт стерлингов', genitive: 'фунта' },
  { code: 'TRY', symbol: '₺',   after: true,  locale: 'tr-TR', label: 'Турецкая лира', genitive: 'лиры' },
];

export const currencyByCode = (code) =>
  CURRENCIES.find((c) => c.code === code) || CURRENCIES[0];

/** Thin-space grouping, matching the "13 395" in the условия price table. */
const group = (n, decimals, locale) =>
  new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(n);

/**
 * Format an amount in `code`. Big numbers lose the decimals nobody reads:
 * 13 395 ₽, but 90,8 BYN — the same rule the approved условия table follows.
 */
export const formatMoney = (amount, code, { showSymbol = true } = {}) => {
  const cur = currencyByCode(code);
  const n = Number(amount) || 0;
  let decimals;
  if (Number.isInteger(n)) decimals = 0;           // $9, not $9,0
  else if (Math.abs(n) >= 100) decimals = 0;       // 13 395 ₽, not 13 395,28 ₽
  else decimals = Math.round(n * 10) === n * 10 ? 1 : 2;
  const text = group(n, decimals, cur.locale);
  // U+00A0, not a plain space. Intl already binds the thousands group with a
  // non-breaking space, so "1 400" holds together — joining the symbol with an
  // ordinary space left a legal break point, and «1 400 ₽» split across lines
  // in the totals and collided with the digits in the hero. Binding it here
  // makes the amount atomic everywhere, with no CSS required to hold it.
  if (!showSymbol) return `${text}\u00A0${cur.code}`;
  return cur.after ? `${text}\u00A0${cur.symbol}` : `${cur.symbol}${text}`;
};

/** The number alone, when the currency is already named by a column heading. */
export const formatBare = (amount, code) => {
  const cur = currencyByCode(code);
  const n = Number(amount) || 0;
  let decimals;
  if (Number.isInteger(n)) decimals = 0;
  else if (Math.abs(n) >= 100) decimals = 0;
  else decimals = Math.round(n * 10) === n * 10 ? 1 : 2;
  return group(n, decimals, cur.locale);
};

/** "1 $ = 81,00 ₽" — the line that replaces screenshotting a Google search. */
export const formatRate = (rate, baseCode, quoteCode) => {
  const base = currencyByCode(baseCode);
  const quote = currencyByCode(quoteCode);
  const shown = new Intl.NumberFormat(quote.locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: rate < 1 ? 4 : 2,
  }).format(Number(rate) || 0);
  const baseUnit = base.after ? `1\u00A0${base.symbol}` : `${base.symbol}1`;
  const quoteUnit = quote.after ? `${shown}\u00A0${quote.symbol}` : `${quote.symbol}${shown}`;
  return `${baseUnit} = ${quoteUnit}`;
};
